import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice du glucose moyen estimé";
const DESCRIPTION =
  "Calculatrice du glucose moyen estimé en francais pour estimer vos indicateurs de sante rapidement. Entrez vos donnees et obtenez un resultat clair avec des reperes utiles pour le suivi quotidien.";
const KEYWORDS =
  "du glucose moyen estimé, calculatrice du glucose moyen estimé, calcul en ligne, sante, bien-etre, indicateurs sante, resultat rapide";
const OG_URL =
  "https://do-calculate.com/calculator/fr/estimated-average-glucose-calculator/";

const diabetesRanges = {
  diabetes: {
    HbA1c: [
      {
        limit: 6,
        message: "✅ Votre niveau HbA1c est inférieur à l'objectif du diabète - bon travail!",
        bg: "alert alert-success",
      },
      { limit: 7, message: "✅ Bon ratio HbA1c", bg: "alert alert-success" },
      {
        limit: 7.5,
        message: "❗ Le ratio HbA1c est généralement très élevé.",
        bg: "alert alert-danger",
      },
      {
        limit: 8,
        message: "❗ Le ratio HbA1c est généralement très élevé.",
        bg: "alert alert-danger",
      },
      {
        limit: Infinity,
        message: "❗ Le ratio HbA1c est généralement très élevé.",
        bg: "alert alert-danger",
      },
    ],
    eAG: [
      {
        limit: 140,
        message: "Le glucose moyen estimé est dans la plage normale.",
        bg: "alert alert-success",
      },
      {
        limit: 154.2,
        message: "Le glucose moyen estimé est bon.",
        bg: "alert alert-success",
      },
      {
        limit: Infinity,
        message: "Le niveau de glucose est élevé.",
        bg: "alert alert-danger",
      },
    ],
  },
  nonDiabetes: {
    HbA1c: [
      {
        limit: 6,
        message: "💚 Votre HbA1c est dans la plage normale.",
        bg: "alert alert-success",
      },
      {
        limit: 6.5,
        message:
          "🔶 Le niveau de HbA1c est en phase de prédiabète. Contactez un médecin, mangez bien et faites de l'exercice.",
        bg: "alert alert-warning",
      },
      {
        limit: Infinity,
        message: "🔺 Votre résultat indique un diabète. Consultez votre médecin.",
        bg: "alert alert-danger",
      },
    ],
    eAG: [
      {
        limit: 116,
        message: "Le niveau de glucose est normal.",
        bg: "alert alert-success",
      },
      {
        limit: Infinity,
        message: "Le niveau de glucose indique un diabète! Contactez le médecin.",
        bg: "alert alert-danger",
      },
    ],
  },
};

function GlucoseCalculator() {
  const [hbA1c, setHbA1c] = useState("");
  const [avgGlucose, setAvgGlucose] = useState("");
  const [diabetesStatus, setDiabetesStatus] = useState("no");
  const [feedback, setFeedback] = useState("");

  const handleHbA1cChange = (e) => {
    setHbA1c(e.target.value);
    setAvgGlucose("");
  };

  const handleAvgGlucoseChange = (e) => {
    setAvgGlucose(e.target.value);
    setHbA1c("");
  };

  const calculateResults = () => {
    if (hbA1c) {
      const hbA1cValue = parseFloat(hbA1c);
      const calculatedEAG = 28.7 * hbA1cValue - 46.7;
      setAvgGlucose(calculatedEAG.toFixed(2));
      provideFeedback(hbA1cValue, "HbA1c");
    } else if (avgGlucose) {
      const avgGlucoseValue = parseFloat(avgGlucose);
      const calculatedHbA1c = (avgGlucoseValue + 46.7) / 28.7;
      setHbA1c(calculatedHbA1c.toFixed(2));
      provideFeedback(avgGlucoseValue, "eAG");
    }
  };

  const provideFeedback = (value, type) => {
    let rangeType = diabetesStatus === "yes" ? "diabetes" : "nonDiabetes";
    let feedbackMessages = diabetesRanges[rangeType][type];
    for (let i = 0; i < feedbackMessages.length; i++) {
      if (value < feedbackMessages[i].limit) {
        setFeedback(feedbackMessages[i]);
        break;
      }
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Qu'est-ce que le glucose moyen estimé (eAG) et qu'est-ce que cela signifie? Le glucose moyen estimé est un calcul qui montre quel aurait été votre niveau moyen de sucre dans le sang au cours des 3 derniers mois en fonction du résultat de votre test HbA1c.
                </p>
                <p>
                  Le test HbA1c mesure le pourcentage d'hémoglobine glyquée (hémoglobine à laquelle du glucose s'est attaché) dans le sang. C'est un indicateur important pour évaluer le contrôle glycémique à long terme.
                </p>
                <p>
                  Notre calculatrice utilise la formule scientifique reconnue pour convertir entre HbA1c et eAG:
                </p>
                <ul>
                  <li>eAG (mg/dL) = 28.7 × HbA1c - 46.7</li>
                  <li>eAG (mmol/L) = 3.46 × HbA1c - 15.2</li>
                </ul>
                <p>
                  Cette calculatrice aide les diabétiques et les professionnels de santé à mieux comprendre les niveaux de glycémie et à gérer le contrôle du diabète de manière plus efficace.
                </p>
                <hr />
              </div>
              <br />
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <div dir="ltr">
                  <div>
                    <hr />
                    <div className="form-group">
                      <label className="form-label ms-2" htmlFor="hbA1c">
                        Entrez HbA1c (%)
                      </label>
                      <input
                        id="hbA1c"
                        type="number"
                        value={hbA1c}
                        onChange={handleHbA1cChange}
                        placeholder="Entrez HbA1c"
                        className="form-control"
                        style={{ width: "75%" }}
                        step="0.1"
                      />
                    </div>
                    <p className="text-center">OU</p>
                    <div className="form-group">
                      <label className="form-label ms-2" htmlFor="avgGlucose">
                        Glucose moyen (mg/dL)
                      </label>
                      <input
                        id="avgGlucose"
                        type="number"
                        value={avgGlucose}
                        onChange={handleAvgGlucoseChange}
                        placeholder="Entrez le glucose moyen"
                        className="form-control"
                        style={{ width: "75%" }}
                        step="1"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label ms-2" htmlFor="diabetesStatus">
                        Statut diabétique
                      </label>
                      <select
                        id="diabetesStatus"
                        value={diabetesStatus}
                        onChange={(e) => setDiabetesStatus(e.target.value)}
                        className="form-control"
                        style={{ width: "75%" }}
                      >
                        <option value="no">Non diabétique</option>
                        <option value="yes">Diabétique</option>
                      </select>
                    </div>
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px", marginLeft: "10px" }}
                      onClick={calculateResults}
                    >
                      Calculer
                    </button>
                  </div>
                  <br />
                  {feedback && (
                    <div className={`p-3 mb-2 ${feedback.bg}`}>
                      <h4>{feedback.message}</h4>
                    </div>
                  )}
                  <hr />
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
  <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conversion des units de glucose</h3></div>
              <div className="info-section-body"><p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p></div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Valeurs normales</h3></div>
              <div className="info-section-body"><p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GlucoseCalculator;
